/**
 * /online-camera — the camera itself: open, switch, read what it can do, take
 * the picture.
 *
 * The door is CameraManager (public/js/camera-manager.js): it owns the
 * getUserMedia watchdog, the relax ladder for drivers that refuse a size, the
 * classification of a failure and the sentence for each cause. This module
 * only decides WHAT to ask for and reads back what it got.
 *
 * Two ways to take the picture, and the rule between them:
 *   • ImageCapture.takePhoto() — the real shutter where the browser has it
 *     (Chrome/Edge; on Android it is the full sensor: 12 Mpx where the stream
 *     gives 2). It can be slow (0.2–1 s), may reconfigure the stream, may come
 *     back smaller than the stream on some desktop webcams, or sideways.
 *   • A frame of the stream — instant, always there.
 * The frame is grabbed FIRST, at the moment of the press; the still replaces it
 * only when it really carries more pixels and the same orientation.
 */

export class Camera {
    constructor(opts) {
        this.video = opts.video;
        this.mgr = new window.CameraManager({ slug: opts.slug || 'online-camera' });
        this.stream = null;
        this.track = null;
        this.imageCapture = null;
        this.photoCaps = null;
        this.caps = {};
        this.settings = {};
        this.initial = {};          // settings right after opening — «reset» returns here
    }

    isSupported() { return this.mgr.isSupported(); }

    /**
     * @param {{deviceId?:string, facingMode?:string, res:{w:number,h:number}, button?:Element}} o
     */
    async open(o) {
        const video = { width: { ideal: o.res.w }, height: { ideal: o.res.h }, frameRate: { ideal: 30 } };
        if (o.deviceId) video.deviceId = { exact: o.deviceId };
        else if (o.facingMode) video.facingMode = { ideal: o.facingMode };
        this.close();
        const stream = await this.mgr.startRaw({ video, audio: false }, o.button ? { button: o.button } : undefined);
        this.stream = stream;
        this.track = stream.getVideoTracks()[0] || null;
        // Frames, not play(): on a camera that never sends one, play() never
        // settles — and the 6-second first-frame wait that followed it never ran,
        // so «Открыть камеру» hung for good. After 6 s of visible time the door
        // says why next to the button and keeps waiting (a slow camera still
        // comes up); a camera that never does is a refusal the page's own panel names.
        if (!(await this.mgr.show(this.video, stream))) {
            this.close();
            const none = new Error('the camera sends no frames');
            none.code = 'unresponsive';
            throw none;
        }
        this.refresh();
        this.initial = Object.assign({}, this.settings);
        this.imageCapture = null; this.photoCaps = null;
        try {
            if (window.ImageCapture && this.track) {
                this.imageCapture = new window.ImageCapture(this.track);
                this.photoCaps = await withTimeout(this.imageCapture.getPhotoCapabilities(), 2500).catch(() => null);
            }
        } catch (_) { this.imageCapture = null; }
        return stream;
    }

    refresh() {
        const t = this.track;
        this.caps = {};
        this.settings = {};
        if (!t) return;
        try { this.caps = t.getCapabilities ? (t.getCapabilities() || {}) : {}; } catch (_) { this.caps = {}; }
        try { this.settings = t.getSettings ? (t.getSettings() || {}) : {}; } catch (_) { this.settings = {}; }
    }

    close() {
        try { this.mgr.stop(); } catch (_) { }
        if (this.stream) { try { this.stream.getTracks().forEach(t => t.stop()); } catch (_) { } }
        this.stream = null; this.track = null; this.imageCapture = null; this.photoCaps = null;
        try { this.video.srcObject = null; } catch (_) { }
    }

    get live() { return !!(this.track && this.track.readyState === 'live' && this.video.videoWidth > 0); }
    get width() { return this.video.videoWidth; }
    get height() { return this.video.videoHeight; }
    /** 'user' | 'environment' | '' (a desktop webcam does not say). */
    get facing() { return this.settings.facingMode || ''; }
    get label() { return (this.track && this.track.label) || ''; }

    async devices() {
        try { return await this.mgr.enumerate(); } catch (_) { return []; }
    }

    /** Largest still the browser promises, or null. */
    stillSize() {
        const pc = this.photoCaps;
        if (!pc || !pc.imageWidth || !pc.imageHeight) return null;
        const w = pc.imageWidth.max, h = pc.imageHeight.max;
        return (w > 0 && h > 0) ? { w, h } : null;
    }
    canTakePhoto() { return !!(this.imageCapture && this.imageCapture.takePhoto); }
    flashModes() {
        const pc = this.photoCaps;
        return (pc && Array.isArray(pc.fillLightMode)) ? pc.fillLightMode : [];
    }

    /** The current frame of the stream, as a canvas of the stream's size. */
    grabFrame() {
        const w = this.width, h = this.height;
        if (!(w > 0 && h > 0)) return null;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const x = c.getContext('2d');
        if (!window.__drawCameraFrame(x, this.video, 0, 0, w, h)) return null;   // an interrupted camera
        return c;
    }

    /** Same, as an ImageBitmap where the browser can (cheaper to hold for bursts). */
    async grabBitmap() {
        if (typeof createImageBitmap === 'function') {
            try { return await createImageBitmap(this.video); } catch (_) { /* fall through */ }
        }
        return this.grabFrame();
    }

    /**
     * The real shutter. Resolves to an ImageBitmap or null (not available,
     * failed, timed out). The browser applies EXIF orientation on decode.
     */
    async takeStill(opts) {
        if (!this.canTakePhoto()) return null;
        const settings = {};
        const s = this.stillSize();
        if (s) { settings.imageWidth = s.w; settings.imageHeight = s.h; }
        if (opts && opts.fillLight && this.flashModes().indexOf(opts.fillLight) >= 0) settings.fillLightMode = opts.fillLight;
        let blob = null;
        try {
            blob = await withTimeout(this.imageCapture.takePhoto(settings), 6000);
        } catch (e) {
            // some drivers reject the size we asked; try once with defaults
            try { blob = await withTimeout(this.imageCapture.takePhoto(), 6000); } catch (e2) { this.lastStillError = String((e2 && (e2.name || e2.message)) || e2); return null; }
        }
        if (!blob || !blob.size) return null;
        try {
            const bmp = await createImageBitmap(blob);
            bmp.__blob = blob;
            return bmp;
        } catch (_) { return null; }
    }

    /** applyConstraints with one advanced set; resolves to true/false and refreshes settings. */
    async apply(set) {
        if (!this.track || !this.track.applyConstraints) return false;
        try {
            await this.track.applyConstraints({ advanced: [set] });
            this.refresh();
            return true;
        } catch (e) {
            this.lastApplyError = String((e && (e.name || e.message)) || e);
            this.refresh();
            return false;
        }
    }

    supportsPointsOfInterest() {
        try {
            const sc = navigator.mediaDevices.getSupportedConstraints ? navigator.mediaDevices.getSupportedConstraints() : {};
            return !!sc.pointsOfInterest;
        } catch (_) { return false; }
    }

    errorMessage(err) {
        const code = (err && err.code) || window.CameraManager.classify(err);
        try { return this.mgr.getErrorMessage(code); } catch (_) { return (err && err.message) || String(err); }
    }
    hasHelp() { try { return this.mgr.hasHelp(); } catch (_) { return false; } }
}

export function withTimeout(p, ms) {
    return new Promise((resolve, reject) => {
        let done = false;
        const t = setTimeout(() => { if (!done) { done = true; const e = new Error('timeout'); e.name = 'TimeoutError'; reject(e); } }, ms);
        Promise.resolve(p).then((v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } },
            (e) => { if (!done) { done = true; clearTimeout(t); reject(e); } });
    });
}
